/**
 * HypothesisBuilderModal — connected wrapper for the pure
 * {@link HypothesisBuilderModalView}. Owns the create / update / run mutations
 * and the post-run refetch, exposing them through a single `onSubmit`.
 *
 * Modes:
 *  • Create (default): POSTs a new hypothesis. "Save & Run" additionally
 *    triggers a run and refetches so the navigated detail page sees the
 *    freshest status.
 *  • Edit (when `hypothesis` is set): PATCHes the existing hypothesis.
 */

import {
  type HypothesisBuilderAction,
  type HypothesisBuilderDraft,
  HypothesisBuilderModalView,
} from '@/components/hypotheses/HypothesisBuilderModalView'
import { hypothesesApi } from '@/services/api/hypotheses'
import type { Hypothesis } from '@/types/hypothesis'
import {
  useCreateHypothesis,
  useTriggerHypothesisRun,
  useUpdateHypothesis,
} from './hooks/use-hypotheses'

export type { HypothesisBuilderAction } from '@/components/hypotheses/HypothesisBuilderModalView'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  caseId: string
  /** When provided, the modal opens in edit mode pre-filled from this
   *  hypothesis and saves via PATCH instead of POST. Omit for create mode. */
  hypothesis?: Hypothesis
  /** Called after a successful save. */
  onSaved?: (hypothesis: Hypothesis, action: HypothesisBuilderAction) => void
}

export function HypothesisBuilderModal({ open, onOpenChange, caseId, hypothesis, onSaved }: Props) {
  const createMutation = useCreateHypothesis(caseId)
  const updateMutation = useUpdateHypothesis(caseId)
  const triggerRunMutation = useTriggerHypothesisRun(caseId)
  const submitting =
    createMutation.isPending || updateMutation.isPending || triggerRunMutation.isPending

  const handleSubmit = async (action: HypothesisBuilderAction, draft: HypothesisBuilderDraft) => {
    if (action === 'edit' && hypothesis) {
      const updated = await updateMutation.mutateAsync({
        id: hypothesis.id,
        patch: {
          title: draft.title,
          caseDescription: draft.caseDescription,
          items: draft.items,
        },
      })
      onSaved?.(updated, 'edit')
      return
    }

    const created = await createMutation.mutateAsync({
      title: draft.title,
      caseDescription: draft.caseDescription,
      items: draft.items,
    })
    if (action === 'run') {
      await triggerRunMutation.mutateAsync(created.id)
      // Re-fetch the now-running hypothesis so the navigated detail page sees
      // the freshest status.
      const refreshed = await hypothesesApi.get(caseId, created.id)
      onSaved?.(refreshed, 'run')
    } else {
      onSaved?.(created, 'draft')
    }
  }

  return (
    <HypothesisBuilderModalView
      open={open}
      onOpenChange={onOpenChange}
      isEdit={!!hypothesis}
      initialValues={
        hypothesis
          ? {
              title: hypothesis.title,
              caseDescription: hypothesis.caseDescription,
              items: hypothesis.items,
            }
          : undefined
      }
      submitting={submitting}
      onSubmit={handleSubmit}
    />
  )
}
